import { useEffect } from "react";
import { isIndexedLocale } from "./i18n";

export const SITE_NAME = "usmalbilder.ch";
export const SITE_URL = "https://usmalbilder.ch";
export const DEFAULT_DESCRIPTION =
  "Kostenlose Ausmalbilder und Malvorlagen für Kinder, Familien und entspannte kreative Pausen.";
export const DEFAULT_IMAGE = `${SITE_URL}/og-default.svg`;

export function absoluteUrl(path = "/") {
  if (!path || path === "/") {
    return SITE_URL;
  }

  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function absolutePageUrl(path = "/") {
  const url = absoluteUrl(path);
  if (url === SITE_URL) {
    return `${SITE_URL}/`;
  }

  return /[?#]$/.test(url) || url.split(/[?#]/, 1)[0].endsWith("/")
    ? url
    : url.replace(/([?#]|$)/, "/$1");
}

export function createBreadcrumbList(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absolutePageUrl(item.path),
    })),
  };
}

export function createCollectionPageSchema({
  title,
  description,
  path,
  items,
  getItemUrl,
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description,
    url: absolutePageUrl(path),
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
    },
    mainEntity: {
      "@type": "ItemList",
      itemListOrder: "https://schema.org/ItemListUnordered",
      numberOfItems: items.length,
      itemListElement: items.slice(0, 24).map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: absolutePageUrl(getItemUrl ? getItemUrl(item) : `/view/${item.downloadUrl.replace(".png", "")}`),
      })),
    },
  };
}

export function createImagePageSchema({
  image,
  categoryName,
  path,
  keywordLabels = ["coloring page", "printable sheet"],
  licensePath = "/nutzung-und-lizenz",
}) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: image.title,
      description: image.description,
      url: absolutePageUrl(path),
      primaryImageOfPage: absoluteUrl(`/${image.downloadUrl}`),
      mainEntity: {
        "@type": "ImageObject",
        name: image.title,
        description: image.description,
        contentUrl: absoluteUrl(`/${image.downloadUrl}`),
        thumbnailUrl: image.thumbnailUrl
          ? absoluteUrl(`/${image.thumbnailUrl}`)
          : DEFAULT_IMAGE,
        creator: {
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
        },
        copyrightNotice: `Copyright ${SITE_NAME}`,
        ...(image.width ? { width: image.width } : {}),
        ...(image.height ? { height: image.height } : {}),
      },
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      name: image.title,
      description: image.description,
      caption: image.description,
      contentUrl: absoluteUrl(`/${image.downloadUrl}`),
      thumbnailUrl: image.thumbnailUrl
        ? absoluteUrl(`/${image.thumbnailUrl}`)
        : DEFAULT_IMAGE,
      encodingFormat: "image/png",
      isFamilyFriendly: true,
      license: absolutePageUrl(licensePath),
      acquireLicensePage: absolutePageUrl(licensePath),
      creditText: SITE_NAME,
      creator: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
      },
      copyrightNotice: `Copyright ${SITE_NAME}`,
      ...(image.width ? { width: image.width } : {}),
      ...(image.height ? { height: image.height } : {}),
      representativeOfPage: true,
      mainEntityOfPage: absolutePageUrl(path),
      keywords: [
        categoryName,
        ...keywordLabels.map((label) => `${categoryName} ${label}`),
      ]
        .filter(Boolean)
        .join(", "),
    },
  ];
}

export function createWebPageSchema({ title, description, path, pageType = "WebPage" }) {
  return {
    "@context": "https://schema.org",
    "@type": pageType,
    name: title,
    description,
    url: absolutePageUrl(path),
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  type = "website",
  image = DEFAULT_IMAGE,
  noindex = false,
  alternates = [],
  structuredData = [],
  preserveExistingHead = false,
}) {
  const alternatesKey = JSON.stringify(alternates);
  const pathLocale = path.match(/^\/(en|fr|it|es|pt|hi)(?:\/|$)/)?.[1] || "de";
  const effectiveNoindex = noindex || !isIndexedLocale(pathLocale);

  useEffect(() => {
    if (preserveExistingHead) {
      return;
    }

    const meta = [
      ["name", "description", description],
      ["property", "og:title", title],
      ["property", "og:description", description],
      ["property", "og:type", type],
      ["property", "og:url", absolutePageUrl(path)],
      ["property", "og:image", image],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", title],
      ["name", "twitter:description", description],
      ["name", "twitter:image", image],
    ];

    document.title = title;

    meta.forEach(([attribute, key, value]) => {
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", value);
    });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", absolutePageUrl(path));

    document
      .head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((node) => node.remove());

    JSON.parse(alternatesKey).forEach((alternate) => {
      if (!alternate?.hreflang || !alternate?.href) {
        return;
      }

      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", alternate.hreflang);
      link.setAttribute("href", absolutePageUrl(alternate.href));
      link.dataset.usmalbilderHreflang = "true";
      document.head.appendChild(link);
    });

    let robots = document.head.querySelector('meta[name="robots"]');
    if (effectiveNoindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex,follow");
    } else if (robots) {
      robots.remove();
    }

    document
      .head
      .querySelectorAll("script[data-usmalbilder-schema]")
      .forEach((node) => node.remove());

    structuredData.filter(Boolean).forEach((item) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.usmalbilderSchema = "true";
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });
  }, [
    alternatesKey,
    description,
    image,
    effectiveNoindex,
    path,
    preserveExistingHead,
    structuredData,
    title,
    type,
  ]);

  return null;
}
